<template>
  <div class="execution-history-card">
    <div class="flex items-start justify-between">
      <!-- Main Info -->
      <div class="flex items-start space-x-4 flex-1">
        <!-- Status Icon -->
        <div class="flex-shrink-0 mt-1">
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center"
            :class="statusIconClass"
          >
            <component :is="statusIcon" class="w-4 h-4" />
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center space-x-2">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {{ formatToolName(execution.toolName) }}
            </h3>
            <span
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
              :class="statusBadgeClass"
            >
              {{ execution.status }}
            </span>
          </div>

          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Executed {{ formatTimeAgo(execution.startTime) }}
          </p>

          <!-- Execution Details -->
          <div class="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
            <span v-if="execution.duration"> Duration: {{ execution.duration }}ms </span>
            <span> ID: {{ execution.id.substring(0, 8) }}... </span>
          </div>

          <!-- Error Message -->
          <div
            v-if="execution.error"
            class="mt-2 p-2 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded text-xs"
          >
            <span class="text-error-700 dark:text-error-300">
              {{ execution.error.message }}
            </span>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center space-x-2 ml-4">
        <button
          class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
          title="View details"
          @click="$emit('view', execution)"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        </button>

        <button
          v-if="execution.status === 'failed'"
          class="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
          title="Retry execution"
          @click="$emit('retry', execution)"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ToolExecution } from '@/types/mcp';

export interface ExecutionHistoryCardProps {
  execution: ToolExecution;
}

const props = defineProps<ExecutionHistoryCardProps>();

defineEmits<{
  view: [execution: ToolExecution];
  retry: [execution: ToolExecution];
}>();

// Computed
const statusIconClass = computed(() => {
  switch (props.execution.status) {
    case 'completed':
      return 'bg-success-100 text-success-600 dark:bg-success-900/50 dark:text-success-400';
    case 'failed':
      return 'bg-error-100 text-error-600 dark:bg-error-900/50 dark:text-error-400';
    case 'running':
      return 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400';
    case 'pending':
      return 'bg-warning-100 text-warning-600 dark:bg-warning-900/50 dark:text-warning-400';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
  }
});

const statusBadgeClass = computed(() => {
  switch (props.execution.status) {
    case 'completed':
      return 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200';
    case 'failed':
      return 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-200';
    case 'running':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'pending':
      return 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
});

const statusIcon = computed(() => {
  switch (props.execution.status) {
    case 'completed':
      return {
        template: `
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        `,
      };
    case 'failed':
      return {
        template: `
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        `,
      };
    case 'running':
      return {
        template: `
          <svg class="animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        `,
      };
    case 'pending':
      return {
        template: `
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
          </svg>
        `,
      };
    default:
      return {
        template: `
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
        `,
      };
  }
});

// Methods
const formatToolName = (toolName: string): string => {
  return toolName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatTimeAgo = (timestamp: string): string => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  }
};
</script>

<style scoped>
.execution-history-card {
  @apply bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4;
  @apply hover:border-gray-300 dark:hover:border-gray-600 transition-colors duration-200;
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .animate-spin {
    animation: none;
  }
}
</style>
